<template>
  <div>
    <div :class="[style.block, style.typeBlock]">
      <span :class="[style.heading, style.onTop]">Вид блока</span>

      <div :class="style.tabStyles">
        <TabStyle
          :icon="rowsIcon"
          :iconTone="isRowsPosition ? 'default' : 'tertiary'"
          :active="isRowsPosition"
          @click="setRowsPosition"
        />

        <TabStyle
          :icon="columnsIcon"
          :iconTone="isColumnPosition ? 'default' : 'tertiary'"
          :active="isColumnPosition"
          @click="setColumnPosition"
        />
      </div>
    </div>

    <div :class="style.block">
      <div :class="style.switchBlock">
        <span :class="style.heading">Изображения</span>

        <SwitchComponent
          :modelValue="hasImages"
          @update:modelValue="setHasImages"
          @change="setHasImages"
        />
      </div>
    </div>

    <div :class="[style.block, style.requiredBottom]">
      <div :class="style.switchBlock">
        <span :class="style.heading">Несколько ответов</span>

        <SwitchComponent
          :modelValue="hasMultipleAnswers"
          @update:modelValue="setHasMultipleAnswers"
          @change="setHasMultipleAnswers"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";

import style from "../../QuestionPanelComponent.module.scss";

import SwitchComponent from "@/elements/UI/input/Switch/SwitchComponent.vue";
import TabStyle from "@/elements/UI/tabs/TabStyle/TabStyle.vue";

import rowsIcon from "$images/icons/tabs/Name=Rows, Size=16.svg";
import columnsIcon from "$images/icons/tabs/Name=Columns, Size=16.svg";

import {questions, QuestionTypes, QuestionVariant} from "@/page/quiz/typeQuiz";

type PositionType = "rows" | "column" | "";

export default defineComponent({
  name: "OpenListQuestionSettings",

  components: {
    SwitchComponent,
    TabStyle
  },

  props: {
    question: {
      type: Object as PropType<questions | null>,
      default: null
    }
  },

  data() {
    return {
      style,
      rowsIcon,
      columnsIcon
    };
  },

  computed: {
    activePosition(): PositionType {
      switch (this.question?.vertical) {
        case true:
          return "column";

        case false:
          return "rows";

        default:
          return "";
      }
    },

    isColumnPosition(): boolean {
      return this.activePosition === "column";
    },

    isRowsPosition(): boolean {
      return this.activePosition === "rows" || this.activePosition === "";
    },

    hasImages(): boolean {
      return (
        this.question?.type === "radio-img" ||
        this.question?.type === "checkbox-img"
      );
    },

    hasMultipleAnswers(): boolean {
      return (
        this.question?.type === "checkbox" ||
        this.question?.type === "checkbox-img"
      );
    },

    variants(): QuestionVariant[] {
      if (!this.question || !Array.isArray(this.question.variants)) {
        return [];
      }

      return this.question.variants as QuestionVariant[];
    }
  },

  methods: {
    setRowsPosition(): void {
      if (!this.question) {
        return;
      }

      this.question.vertical = false;
    },

    setColumnPosition(): void {
      if (!this.question) {
        return;
      }

      this.question.vertical = true;
    },

    setHasImages(value: boolean | Event): void {
      if (!this.question) {
        return;
      }

      const hasImages = this.getSwitchValue(value);

      this.question.type = this.getQuestionType({
        hasImages,
        hasMultipleAnswers: this.hasMultipleAnswers
      });

      const variants = this.normalizeVariants(hasImages);

      this.question.variants = variants;
    },

    setHasMultipleAnswers(value: boolean | Event): void {
      if (!this.question) {
        return;
      }

      const hasMultipleAnswers = this.getSwitchValue(value);

      this.question.type = this.getQuestionType({
        hasImages: this.hasImages,
        hasMultipleAnswers
      });
    },

    getQuestionType(options: {
      hasImages: boolean;
      hasMultipleAnswers: boolean;
    }): QuestionTypes {
      if (options.hasImages && options.hasMultipleAnswers) {
        return "checkbox-img";
      }

      if (options.hasImages && !options.hasMultipleAnswers) {
        return "radio-img";
      }

      if (!options.hasImages && options.hasMultipleAnswers) {
        return "checkbox";
      }

      return "radio";
    },

    getSwitchValue(value: boolean | Event): boolean {
      if (value instanceof Event) {
        return Boolean((value.target as HTMLInputElement | null)?.checked);
      }

      return Boolean(value);
    },

    normalizeVariants(hasImages: boolean) {
      return this.variants.map((variant) => {
        if (!hasImages && variant.img?.startsWith("blob:")) {
          URL.revokeObjectURL(variant.img);
        }

        return {
          text: variant.text || "",
          img: hasImages ? variant.img || "" : "",
          _key: variant._key
        };
      });
    }
  }
});
</script>
